import router from '@/router'
import { AppHeader } from '@/components'
import { allowOnlyGuests } from '@/router/routerGuards'
import { swoopAPI } from '@/api/index'
import store from '@/store'
import { companyGetters } from '@/store/modules/company/routines'
import { LSKeys } from '@/utils/constants'
import { trackEvent } from '@/composables/segmentTracking'
import onboardingConfig from './config'
import * as Sentry from '@sentry/vue'

const handleLegacyFormRedirect = to => {
  let formRoute = to.params.formRoute || to.params.goal

  if (formRoute === 'equity') {
    formRoute = 'findInvestor'
  } else if (formRoute === 'grant' || formRoute === 'grants') {
    formRoute = 'findGrant'
  }

  return { name: 'get-started', query: { fundingReason: formRoute, ...to.query } }
}

// We used to show a Swoop for Business header when in the swoop whitelabel,
// but it never worked due to it is implemented, so we removed it

// Setup routes
export default () => {
  router.addRoute({
    path: '/stage',
    name: 'stage',
    components: {
      default: () => import('./pages/Stage.vue'),
      header: AppHeader
    },
    alias: ['/goals', '/goal', '/welcome'],
    beforeEnter: allowOnlyGuests()
  })
  router.addRoute({
    path: '/get-started',
    name: 'get-started',
    components: {
      default: () => import('./pages/GetStarted.vue'),
      header: AppHeader
    },
    beforeEnter: async (to, _from, next) => {
      if (to.query.integrationReturn !== 'true') return next()

      const companyId = store.getters[companyGetters.COMPANY_ID]
      if (!companyId) return next()

      try {
        const { data } = await swoopAPI.get(`api/v1/forms/in-progress-onboarding/${companyId}`)
        if (!data.isThereAnInProgressOnboarding) {
          let prevRoute = { name: 'dashboard' }
          try {
            const saved = localStorage.getItem(LSKeys.apiDeckIntegrationPrevRoute)
            if (saved) prevRoute = JSON.parse(saved)
          } catch (e) {
            Sentry.captureException(e, { extra: { context: 'get-started beforeEnter: parsing apiDeckIntegrationPrevRoute' } })
          }
          localStorage.removeItem(LSKeys.apiDeckIntegrationPrevRoute)
          // Onboarding completed by connecting an integration as the final step. GetStartedForm never mounts on
          // this path, so fire the completion event here before redirecting.
          trackEvent('Completed Signup', {
            fundingReason: to.query.fundingReason || '',
            version: to.query.version || '',
            companyId,
            completedViaIntegration: true
          })
          const journeyDestination = onboardingConfig.journeyDestinations[to.query.fundingReason]
          return next(journeyDestination || prevRoute)
        }
      } catch (e) {
        Sentry.captureException(e, { extra: { context: 'get-started beforeEnter: in-progress-onboarding check', companyId } })
      }

      next()
    }
  })
  router.addRoute({
    path: '/make-a-change',
    name: 'make-a-change',
    components: {
      default: () => import('./pages/EditAnswers.vue'),
      header: AppHeader
    },
    meta: {
      permissions: {
        roles: ['user'],
        redirectTo: {
          name: 'login'
        }
      }
    },
    props: {
      formType: 'onboarding'
    }
  })
  router.addRoute({
    path: '/edit-funding-details',
    name: 'edit-funding-details',
    components: {
      default: () => import('./pages/EditFundingDetails.vue'),
      header: AppHeader
    },
    meta: {
      permissions: {
        roles: ['user'],
        redirectTo: {
          name: 'login'
        }
      }
    }
  })
  router.addRoute({
    path: '/edit-details-page',
    name: 'edit-details-page',
    components: {
      default: () => import('./pages/DetailsPage.vue'),
      header: AppHeader
    },
    meta: {
      permissions: {
        roles: ['user'],
        redirectTo: {
          name: 'login'
        }
      }
    }
  })
  router.addRoute({
    path: '/details-page',
    name: 'details-page',
    components: {
      default: () => import('./pages/DetailsPage.vue'),
      header: AppHeader
    },
    meta: {
      permissions: {
        roles: ['user'],
        redirectTo: {
          name: 'login'
        }
      }
    }
  })
  // back entrance
  router.addRoute({
    path: '/sign-up',
    name: 'sign-up',
    components: {
      default: () => import('./pages/SignUp.vue'),
      header: AppHeader
    },
    alias: ['/sign-up/user']
  })
  router.addRoute({
    path: '/sage-onboarding',
    name: 'sage-onboarding',
    components: {
      default: () => import('./pages/SageOnboarding.vue'),
      header: AppHeader
    },
    alias: ['/sign-up/sage']
  })
  router.addRoute({
    path: '/goal:goal',
    name: 'goal',
    redirect: handleLegacyFormRedirect
  })
  router.addRoute({
    path: '/goal/:formRoute',
    name: 'goals-form',
    redirect: handleLegacyFormRedirect
  })
  router.addRoute({
    path: '/goal/:formRoute/sign-up',
    name: 'goals-form-sign-up',
    redirect: handleLegacyFormRedirect
  })
}
